import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import { isBlockedLoginError, wipeLocalCompanyData } from "../shared/accessWipe";
import MobilePageHeader from "./components/MobilePageHeader";

/* ============================================================
   MobileAuthPage
   ----------------------------------------------------------------
   Mobile-native sign-in screen. Replaces the desktop AuthPage on
   iPhone-sized viewports — same Supabase auth calls
   (signInWithPassword / signUp / resetPasswordForEmail), single
   column, big tappable controls, mobile-theme tokens.

   Rendered by MobileApp.tsx's auth gate when there is no session.
   On successful sign-in supabase-js fires SIGNED_IN, MobileApp's
   onAuthStateChange subscription flips `session` to non-null, and
   the gate transitions back into the normal mobile shell — the
   settings bootstrap re-runs automatically (see MobileApp.tsx).

   Desktop AuthPage is unchanged.
   ============================================================ */

type Mode = "signIn" | "signUp" | "reset";
type Status =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "info"; message: string }
  | { kind: "error"; message: string };

const normalizeEmail = (v: string) => v.trim().toLowerCase();

export default function MobileAuthPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [mode, setMode] = useState<Mode>("signIn");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const busy = status.kind === "busy";

  const handleLogIn = async () => {
    const e = normalizeEmail(email);
    if (!e.includes("@")) {
      setStatus({ kind: "error", message: "Please enter a valid email." });
      return;
    }
    if (!password) {
      setStatus({ kind: "error", message: "Please enter your password." });
      return;
    }
    setStatus({ kind: "busy" });
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: e,
        password,
      });
      if (error) throw error;
      // No success message — the auth gate will unmount this page as
      // soon as SIGNED_IN fires on the parent's subscription.
    } catch (err: any) {
      if (isBlockedLoginError(err)) {
        // An admin used "Remove access": forget the company's data here.
        wipeLocalCompanyData();
        setStatus({ kind: "error", message: "This login has been blocked by your company's admin. Contact them if you think this is a mistake." });
        return;
      }
      setStatus({
        kind: "error",
        message: friendlyAuthError(err),
      });
    }
  };

  const handleSignUp = async () => {
    const e = normalizeEmail(email);
    if (!e.includes("@")) {
      setStatus({ kind: "error", message: "Please enter a valid email." });
      return;
    }
    if (!password || password.length < 8) {
      setStatus({
        kind: "error",
        message: "Password must be at least 8 characters.",
      });
      return;
    }
    setStatus({ kind: "busy" });
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const { error } = await supabase.auth.signUp({
        email: e,
        password,
        options: { emailRedirectTo: origin },
      });
      if (error) throw error;
      setStatus({
        kind: "info",
        message:
          "Account created. If email confirmation is on, check your inbox — otherwise you can log in now.",
      });
      setMode("signIn");
    } catch (err: any) {
      setStatus({ kind: "error", message: friendlyAuthError(err) });
    }
  };

  const handleReset = async () => {
    const e = normalizeEmail(email);
    if (!e.includes("@")) {
      setStatus({ kind: "error", message: "Enter your email above first." });
      return;
    }
    setStatus({ kind: "busy" });
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const { error } = await supabase.auth.resetPasswordForEmail(e, {
        redirectTo: `${origin}/reset-password`,
      });
      if (error) throw error;
      setStatus({
        kind: "info",
        message: "Password reset email sent. Check your inbox.",
      });
      setMode("signIn");
    } catch (err: any) {
      setStatus({ kind: "error", message: friendlyAuthError(err) });
    }
  };

  const onEnterKey = (handler: () => void) =>
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handler();
    };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  // Page-level title that switches with the auth mode so the user
  // always sees the same kind of title bar they'll see once signed in.
  // The signed-out screens stay chromeless (no theme toggle) — that
  // matches the brand-y feel of the login page.
  const pageTitle =
    mode === "signUp"
      ? "Create account"
      : mode === "reset"
      ? "Reset password"
      : "Log in";
  const backToSignIn =
    mode === "signIn"
      ? undefined
      : {
          label: "Log in",
          onClick: () => {
            setMode("signIn");
            setStatus({ kind: "idle" });
          },
        };

  return (
    <div className="m-auth" data-mode={mode}>
      <MobilePageHeader
        title={pageTitle}
        back={backToSignIn}
        showThemeToggle={false}
      />
      <div className="m-auth-scroll">
        <div className="m-auth-brand">
          <img
            src={`${process.env.PUBLIC_URL}/icon-192.png`}
            alt=""
            className="m-auth-logo"
          />
          <div className="m-auth-brand-name">The Estimator</div>
        </div>

        <section className="m-card m-auth-card" aria-label="Sign in">
          <h2 className="m-auth-title">
            {mode === "signUp"
              ? "Create account"
              : mode === "reset"
                ? "Reset password"
                : "Log in"}
          </h2>

          <div className="m-field">
            <label className="m-section-label" htmlFor="m-auth-email">
              Email
            </label>
            <input
              id="m-auth-email"
              type="email"
              className="m-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
              inputMode="email"
              disabled={busy}
              onKeyDown={onEnterKey(
                mode === "reset"
                  ? handleReset
                  : mode === "signUp"
                    ? handleSignUp
                    : handleLogIn
              )}
            />
          </div>

          {mode !== "reset" && (
            <div className="m-field">
              <label className="m-section-label" htmlFor="m-auth-pw">
                Password
              </label>
              <div className="m-auth-pw-row">
                <input
                  id="m-auth-pw"
                  type={showPw ? "text" : "password"}
                  className="m-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete={
                    mode === "signUp" ? "new-password" : "current-password"
                  }
                  disabled={busy}
                  onKeyDown={onEnterKey(
                    mode === "signUp" ? handleSignUp : handleLogIn
                  )}
                />
                <button
                  type="button"
                  className="m-auth-pw-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  disabled={busy}
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          )}

          {/* Status surface — inline, no native alerts. */}
          {status.kind === "error" && (
            <div className="m-auth-msg m-auth-msg--err" role="alert">
              {status.message}
            </div>
          )}
          {status.kind === "info" && (
            <div className="m-auth-msg m-auth-msg--info" role="status">
              {status.message}
            </div>
          )}

          {/* Primary action — flips with mode. */}
          {mode === "reset" ? (
            <button
              type="button"
              className="m-auth-primary"
              onClick={handleReset}
              disabled={busy}
            >
              {busy ? "Sending…" : "Send reset email"}
            </button>
          ) : mode === "signUp" ? (
            <button
              type="button"
              className="m-auth-primary"
              onClick={handleSignUp}
              disabled={busy}
            >
              {busy ? "Working…" : "Create account"}
            </button>
          ) : (
            <button
              type="button"
              className="m-auth-primary"
              onClick={handleLogIn}
              disabled={busy}
            >
              {busy ? "Signing in…" : "Log in"}
            </button>
          )}

          {/* Mode switchers — small text links so they don't compete
              with the primary button visually. */}
          <div className="m-auth-links">
            {mode === "signIn" ? (
              <>
                <button
                  type="button"
                  className="m-auth-link"
                  onClick={() => {
                    setMode("reset");
                    setStatus({ kind: "idle" });
                  }}
                  disabled={busy}
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  className="m-auth-link"
                  onClick={() => {
                    setMode("signUp");
                    setStatus({ kind: "idle" });
                  }}
                  disabled={busy}
                >
                  Create account
                </button>
              </>
            ) : (
              <button
                type="button"
                className="m-auth-link"
                onClick={() => {
                  setMode("signIn");
                  setStatus({ kind: "idle" });
                }}
                disabled={busy}
              >
                ← Back to log in
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------

/**
 * Translate raw Supabase auth errors into a sentence a person can
 * actually act on. Falls back to the raw message if nothing matches.
 */
function friendlyAuthError(err: any): string {
  const raw = String(err?.message || err || "").trim();
  if (!raw) return "Something went wrong. Please try again.";
  if (/invalid login credentials/i.test(raw))
    return "Wrong email or password. Try again.";
  if (/email not confirmed/i.test(raw))
    return "Your email isn't confirmed yet — check your inbox.";
  if (/already registered|already exists/i.test(raw))
    return "That email already has an account. Try logging in.";
  if (/rate limit|too many/i.test(raw))
    return "Too many attempts. Wait a minute and try again.";
  if (/network|failed to fetch/i.test(raw))
    return "Network problem. Check your connection and try again.";
  return raw;
}
